import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  FeatherGrid,
  FeatherUsers,
  FeatherTrendingUp,
  FeatherTag,
  FeatherClock,
  FeatherRefreshCw
} from '../ui/icons';
import { partnerCompaniesApi } from '../../services/api';
import './TechStackCompanies.css';

const TechStackCompaniesDual = forwardRef(({ techStack, engagementType, hideHeader = false }, ref) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchCompanies = useCallback(async () => {
    if (!techStack || techStack.length === 0) return;

    try {
      setLoading(true);
      setError('');
      
      const techStackString = techStack.join(',');
      const response = await partnerCompaniesApi.getByTechStack(techStackString);
      
      const newCompanies = response.companies || [];
      
      // Only update if the data has actually changed
      setCompanies(prevCompanies => {
        // Quick length check
        if (prevCompanies.length !== newCompanies.length) {
          return newCompanies;
        }
        
        // Deep comparison of company data
        const hasChanged = newCompanies.some((newCompany, index) => {
          const prevCompany = prevCompanies[index];
          return !prevCompany || 
                 prevCompany.id !== newCompany.id ||
                 prevCompany.match_score !== newCompany.match_score ||
                 prevCompany.current_capacity !== newCompany.current_capacity;
        });
        
        return hasChanged ? newCompanies : prevCompanies;
      });
    } catch (err) {
      console.error('Error fetching companies by tech stack:', err);
      setError('Failed to fetch companies that support this tech stack');
    } finally {
      // Add a small delay to prevent flickering for very fast responses
      setTimeout(() => {
        setLoading(false);
      }, 300);
    }
  }, [techStack]);

  useEffect(() => {
    if (techStack && techStack.length > 0) {
      fetchCompanies();
    }
  }, [techStack, fetchCompanies]);

  const handleRefresh = async () => {
    return fetchCompanies();
  };

  // Expose refresh function to parent component
  useImperativeHandle(ref, () => ({
    handleRefresh
  }));

  const getCapacityColor = (capacity) => {
    switch (capacity) {
      case 'free': return 'green';
      case 'partial': return 'yellow';
      case 'fully_booked': return 'red';
      default: return 'gray';
    }
  };

  const getCapacityLabel = (capacity) => {
    switch (capacity) {
      case 'free': return 'Available';
      case 'partial': return 'Limited';
      case 'fully_booked': return 'Booked';
      default: return 'Unknown';
    }
  };

  const formatRateRange = (rateRanges) => {
    if (!rateRanges || typeof rateRanges !== 'object') return 'Contact for rates';
    
    const rates = [];
    if (rateRanges.junior) rates.push(`Junior: $${rateRanges.junior.min}-${rateRanges.junior.max}/hr`);
    if (rateRanges.mid) rates.push(`Mid: $${rateRanges.mid.min}-${rateRanges.mid.max}/hr`);
    if (rateRanges.senior) rates.push(`Senior: $${rateRanges.senior.min}-${rateRanges.senior.max}/hr`);
    if (rateRanges.expert) rates.push(`Expert: $${rateRanges.expert.min}-${rateRanges.expert.max}/hr`);
    
    return rates.length > 0 ? rates.join(', ') : 'Contact for rates';
  };

  // Sort companies by match score for better display
  const sortedCompanies = companies.sort((a, b) => b.match_score - a.match_score);

  const renderCompanyCard = (company) => (
    <div key={company.id} className="nexgate-detail-company-card">
      <div className="nexgate-detail-company-header">
        <div className="nexgate-detail-company-info">
          <h4 className="nexgate-detail-company-name">{company.name}</h4>
          <div className="nexgate-detail-company-meta">
            {company.industry && (
              <span className="nexgate-detail-industry">{company.industry}</span>
            )}
            {company.location && (
              <span className="nexgate-detail-location">
                <FeatherTag size={12} />
                {company.location}
              </span>
            )}
          </div>
        </div>
        <div className="nexgate-detail-company-score">
          <Badge variant="blue" size="sm">
            {company.match_score}% Match
          </Badge>
        </div>
      </div>

      <div className="nexgate-detail-company-details">
        <div className="nexgate-detail-detail-row">
          <div className="nexgate-detail-detail-item">
            <FeatherUsers size={14} />
            <span>Team: {company.team_size || 'N/A'}</span>
          </div>
          <div className="nexgate-detail-detail-item">
            <FeatherClock size={14} />
            <span>Capacity: </span>
            <Badge 
              variant={getCapacityColor(company.current_capacity)} 
              size="sm"
            >
              {getCapacityLabel(company.current_capacity)}
            </Badge>
          </div>
        </div>

        <div className="nexgate-detail-detail-row">
          <div className="nexgate-detail-detail-item">
            <FeatherTrendingUp size={14} />
            <span>Rates: {formatRateRange(company.rate_ranges)}</span>
          </div>
        </div>

        {company.matching_technologies && company.matching_technologies.length > 0 && (
          <div className="nexgate-detail-matching-tech">
            <span className="label">Matching Technologies:</span>
            <div className="nexgate-detail-tech-tags">
              {company.matching_technologies.slice(0, 3).map(tech => (
                <Badge key={tech} variant="outline" size="sm">
                  {tech}
                </Badge>
              ))}
              {company.matching_technologies.length > 3 && (
                <span className="nexgate-detail-more-tech">+{company.matching_technologies.length - 3} more</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (!techStack || techStack.length === 0) {
    return null;
  }

  return (
    <div className="tech-stack-companies-dual">
      {/* Header with refresh button - only show if not hidden */}
      {!hideHeader && (
        <div className="nexgate-detail-tech-stack-companies-dual dual-header">
          <div className="header-content">
            <h3 className="nexgate-detail-tech-stack-companies-dual section-title">
              <FeatherGrid size={20} />
              Companies Supporting Tech Stack
            </h3>
            <div className="nexgate-detail-tech-stack-companies-dual header-actions">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                disabled={loading}
              >
                <FeatherRefreshCw size={16} className={loading ? 'spinning' : ''} />
                Refresh
              </Button>
            </div>
          </div>
          <div className="nexgate-detail-tech-stack-companies-dual tech-stack-tags">
            {techStack.map(tech => (
              <Badge key={tech} variant="secondary" size="sm">
                {tech}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="nexgate-detail-error-state">
          <p>{error}</p>
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            Try Again
          </Button>
        </div>
      )}

      {!error && companies.length === 0 && !loading && (
        <div className="nexgate-detail-empty-state">
          <FeatherGrid size={48} />
          <h4>No Companies Found</h4>
          <p>No partner companies currently support this tech stack.</p>
        </div>
      )}

      <div className="nexgate-detail-tech-companies-grid">
        {companies.length > 0 && sortedCompanies.map(renderCompanyCard)}
        {loading && (
          <div className="nexgate-detail-loading-overlay">
            <div className="nexgate-detail-spinner"></div>
            <p>Refreshing companies...</p>
          </div>
        )}
      </div>
    </div>
  );
});

export default TechStackCompaniesDual; 